import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import CancelSaveButton from '../misc/CancelSaveButton';
import Input from '../misc/Input';
import Slider from '../misc/Slider';
import ApiImage from '../misc/ApiImage';
import photoAlbumService from '../../../services/photoAlbumService';

interface EditAlbumModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  album: PhotoAlbumWithItems;
  onSave: (updatedAlbum: Partial<PhotoAlbumWithItems>) => void;
}

export default function EditAlbumModal({ 
  open, 
  setOpen, 
  album,
  onSave
}: EditAlbumModalProps): JSX.Element {
  const { t } = useTranslation();
  const [title, setTitle] = useState(album.title);
  const [description, setDescription] = useState(album.description || '');
  const [openToUpload, setOpenToUpload] = useState(album.openToUpload);
  const [coverPhoto, setCoverPhoto] = useState<File | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('component.modal.editAlbum.invalidImageType'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error(t('component.modal.editAlbum.imageTooLarge'));
      return;
    }

    try {
      const { resizeImage } = await import('../../../utils/formatUtils');
      const resizedFile = await resizeImage(file, 768);
      
      setCoverPhoto(resizedFile);
      setCoverPhotoPreview(URL.createObjectURL(resizedFile));
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error(t('component.modal.editAlbum.imageProcessingError'));
    }
  };

  const resetForm = () => {
    setTitle(album.title);
    setDescription(album.description || '');
    setOpenToUpload(album.openToUpload);
    setCoverPhoto(null);
    if (coverPhotoPreview) {
      URL.revokeObjectURL(coverPhotoPreview);
      setCoverPhotoPreview(null);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(t('modal.editAlbum.titleInvalid'));
      return;
    }

    if (!title.match(/^[a-zA-Z0-9 ]+$/)) {
      toast.error(t('component.modal.editAlbum.titleInvalid'));
      return;
    }

    if (title.length > 50) {
      toast.error(t('component.modal.editAlbum.titleInvalid'));
      return;
    }

    if (title.length < 3) {
      toast.error(t('component.modal.editAlbum.titleInvalid'));
      return;
    }

    setIsLoading(true);
    try {
      const updatedAlbum = {
        title: title.trim(),
        description: description.trim(),
        openToUpload,
      };

      await photoAlbumService.updatePhotoAlbum(album.id, {
        title: updatedAlbum.title,
        description: updatedAlbum.description,
        openToUpload: updatedAlbum.openToUpload,
      });

      let updatedCoverUrl = album.coverUrl;
      if (coverPhoto) {
        const updatedAlbumWithCover = await photoAlbumService.updatePhotoAlbumCover(album.id, coverPhoto);
        updatedCoverUrl = updatedAlbumWithCover.coverUrl;
      }

      toast.success(t('page.photoAlbum.updated'));
      onSave({ ...updatedAlbum, coverUrl: updatedCoverUrl });
      setOpen(false);
    } catch (error) {
      console.error('Error saving album:', error);
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    resetForm();
    setOpen(false);
  };

  useEffect(() => {
    return () => {
      if (coverPhotoPreview) {
        URL.revokeObjectURL(coverPhotoPreview);
      }
    };
  }, [coverPhotoPreview]);

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 w-full max-w-lg rounded-[20px] bg-secondary-50 p-6"
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-300 bg-opacity-30">
          <FaEdit className="h-6 w-6 text-primary-900" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-primary-900">
            {t('component.modal.editAlbum.title')}
          </h3>
          <p className="text-sm text-primary-900 text-opacity-80">
            {t('component.modal.editAlbum.subtitle')}
          </p>
        </div>
      </div>

      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-primary-900">
          {t('component.modal.editAlbum.coverImage')}
        </label>
        <div className="flex items-center gap-4">
          <div className="relative h-20 w-20 overflow-hidden rounded-lg bg-primary-300 bg-opacity-20">
            {coverPhotoPreview ? (
              <img
                src={coverPhotoPreview}
                alt="New cover preview"
                className="h-full w-full object-cover"
              />
            ) : album.coverUrl ? (
              <ApiImage
                path={album.coverUrl}
                alt={album.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <FaCamera className="h-6 w-6 text-primary-900 opacity-50" />
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <label className="cursor-pointer rounded-lg bg-primary-300 bg-opacity-30 px-3 py-2 text-sm font-medium text-primary-900 transition-colors hover:bg-opacity-40">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              {t('component.modal.editAlbum.changeCover')}
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Input
          id="album-title"
          label={t('general.name')}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full"
          required
        />

        <Input
          id="album-description"
          label={t('general.description')}
          type="textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full h-24"
        />

        <Slider
          label={t('component.modal.editAlbum.uploadSetting')}
          items={[
            { label: t('component.modal.editAlbum.uploadSettingOpen'), value: true },
            { label: t('component.modal.editAlbum.uploadSettingRestricted'), value: false }
          ]}
          value={openToUpload}
          onChange={(value) => setOpenToUpload(Boolean(value))}
        />
      </div>

      <div className="mt-6 flex justify-end">
        <CancelSaveButton
          onCancel={handleCancel}
          onSave={handleSave}
          disabled={isLoading || !title.trim()}
          buttonText={t('general.save')}
          cancelText={t('general.cancel')}
        />
      </div>
    </ModalLayout>
  );
}
